import type { FunctionComponent } from 'react';
import { useFormContext } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { SkillProficiencyLevel, SkillShort } from '~/shared/data-contract/skill';
import { Spinner } from '~/ui/core/spinner/Spinner';

import type { LearningOpportunityFormState } from '../form-state';
import { LearningOpportunitySkillRow } from './LearningOpportunitySkillRow';
import { TrainedSkillsOptions } from './TrainedSkillsOptions';

export const SkillsEditor: FunctionComponent<{
    readonly disabled?: boolean;
    readonly isDetectionPending?: boolean;
    readonly languageCode?: string;
    readonly onSkillClick?: (skillId: string, preferredLanguage?: string) => void;
    readonly onSkillRemoved?: (skillId: string) => void;
}> = ({ disabled = false, isDetectionPending = false, languageCode, onSkillClick, onSkillRemoved }) => {
    const translate = useTranslate();
    const { setValue, watch } = useFormContext<LearningOpportunityFormState>();
    const skills = watch('skills') ?? [];

    const removeSkill = (id: string): void => {
        onSkillRemoved?.(id);
        setValue(
            'skills',
            skills.filter((skill) => skill.id !== id),
            { shouldDirty: true },
        );
    };
    const changeSkillLevel = (id: string, level: SkillProficiencyLevel): void => {
        setValue(
            'skills',
            skills.map((skill) => (skill.id === id ? ({ ...skill, level } as SkillShort) : skill)),
            { shouldDirty: true, shouldValidate: true },
        );
    };

    return (
        <div className="flex flex-col gap-m">
            <div className="flex items-center justify-between">
                <h3 className="m-0 font-medium text-text-primary">
                    {translate('pages.learning-opportunities.trained-skills')}
                </h3>
                <div className="flex items-center gap-s">
                    {isDetectionPending && <Spinner />}
                    {!disabled && <TrainedSkillsOptions languageCode={languageCode} />}
                </div>
            </div>
            {skills.length === 0 ? (
                <div className="rounded-lg bg-surface-hover p-l text-text-content-subtle text-sm">
                    {translate('components.skills.no-skills-attached')}
                </div>
            ) : (
                <div className="flex flex-col gap-xs">
                    {skills.map((skill: SkillShort) => (
                        <LearningOpportunitySkillRow
                            key={skill.id}
                            isEditable={!disabled}
                            languageCode={languageCode}
                            skill={skill}
                            onClick={onSkillClick}
                            onLevelChange={changeSkillLevel}
                            onRemove={disabled ? undefined : removeSkill}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
